import React, { useState } from 'react';
import PageTemplate from '../../components/Common/PageTemplate';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

const modalOverlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalContentVariants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
};

const UniversityModal = ({ department, universities, coordinator, onClose }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={modalOverlayVariants}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        variants={modalContentVariants}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{department}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">{t('erasmus.programCoordinator')}</h3>
          <div class="flex items-center">
            <img src={coordinator.photo} alt={coordinator.name} class="w-16 h-16 rounded-full mr-4"/>
            <div>
              <p class="font-bold text-gray-800">{coordinator.name}</p>
              {coordinator.email && <a href={`mailto:${coordinator.email}`} class="text-sm text-blue-600 hover:underline">{coordinator.email}</a>}
            </div>
          </div>
        </div>
        <div className="p-6 overflow-y-auto">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">{t('erasmus.partnerUniversities')}</h3>
          <ul className="space-y-4">
            {universities.map((uni, index) => (
              <li key={index} className="flex items-center bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <img
                  src={`https://flagcdn.com/w40/${uni.country}.png`}
                  alt={`${uni.country} flag`}
                  className="w-10 h-auto mr-4 rounded"
                />
                <span className="text-lg text-gray-700">{uni.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ErasmusPage = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const programs = [
    {
      name: t('departments.actuarial_science'),
      image: '/aktuerya.jpg',
      bgColor: 'bg-amber-800',
      coordinator: { name: 'Doç. Dr. Başak BULUT KARAGEYİK', photo: 'https://avesis.hacettepe.edu.tr/user/image/8452', email: 'basakbulut@hacettepe.edu.tr' },
      universities: [
          { name: 'CZESTOCHOWA UNIVERSITY OF TECHNOLOGY', country: 'pl' },
          { name: 'University of Piraeus', country: 'gr' },
      ]
    },
    {
      name: t('departments.biology'),
      image: '/biology.jpg',
      bgColor: 'bg-emerald-800',
      coordinator: { name: 'Doç. Dr. Sırma ÇAPAR DİNÇER', photo: 'https://avesis.hacettepe.edu.tr/user/image/6118' },
      universities: [
          { name: 'UNIVERSITE DE MONS', country: 'be' },
          { name: 'Universitat Duisburg-Essen', country: 'de' },
          { name: 'Johannes Gutenberg- Uniiversitat Mainz', country: 'de' },
          { name: 'Universidad de Sevilla', country: 'es' },
          { name: 'Institut Polytechnique LaSalle Beauvais - ESITPA', country: 'fr' },
          { name: 'CZESTOCHOWA UNIVERSITY OF TECHNOLOGY', country: 'pl' },
      ]
    },
    {
      name: t('departments.statistics'),
      image: '/statistic.jpg',
      bgColor: 'bg-sky-800',
      coordinator: { name: 'Dr. Öğr. Üyesi Ceren EDA CAN', photo: 'https://avesis.hacettepe.edu.tr/user/image/6343', email: 'cerencan@hacettepe.edu.tr' },
      universities: [
          { name: 'Universität Bremen', country: 'de' },
          { name: 'Ludwig-Maximilians-Universität München', country: 'de' },
          { name: 'University of Huelva', country: 'es' },
          { name: 'Universidade da Coruna', country: 'es' },
          { name: 'Universidad Complutense de Madrid', country: 'es' },
          { name: 'University of Piraeus', country: 'gr' },
          { name: 'Sapienza Università di Roma', country: 'it' },
          { name: 'Universidade Nova de Lisboa', country: 'pt' },
      ]
    },
    {
      name: t('departments.chemistry'),
      image: '/chemistry.jpg',
      bgColor: 'bg-purple-800',
      coordinator: { name: 'Doç. Dr. Mehmet Atakay', photo: 'https://avesis.hacettepe.edu.tr/user/image/5828', email: 'mehmetatakay@hacettepe.edu.tr' },
      universities: [
          { name: 'Universität Duisburg-Essen', country: 'de' },
          { name: 'Albert-Ludwigs-Universität Freiburg', country: 'de' },
          { name: 'Universitat Rovira i Virgili', country: 'es' },
          { name: 'Université de Tours', country: 'fr' },
          { name: 'Kaunas University of Technology', country: 'lt' },
          { name: 'Wroclaw University of Technology', country: 'pl' },
          { name: 'Umea University', country: 'se' },
      ]
    },
    {
      name: t('departments.mathematics'),
      image: '/matematik.jpg',
      bgColor: 'bg-red-800',
      coordinator: { name: 'Doç. Dr. Berke Kaleboğaz', photo: 'https://avesis.hacettepe.edu.tr/user/image/5564', email: 'bkuru@hacettepe.edu.tr' },
      universities: [
          { name: 'Johannes Gutenberg- Uniiversitat Mainz', country: 'de' },
          { name: 'Universitat de Barcelona', country: 'es' },
          { name: 'Universidad de Cádiz', country: 'es' },
          { name: 'University of Patras', country: 'gr' },
          { name: 'Universita Degli Studi Di Cagliari', country: 'it' },
          { name: 'Universita Degli Studi di Padova', country: 'it' },
          { name: 'CZESTOCHOWA UNIVERSITY OF TECHNOLOGY', country: 'pl' },
          { name: 'Universitatea Babeș-Bolyai', country: 'ro' },
      ]
    },
  ];

  const handleCardClick = (department) => {
    setSelectedDepartment(department);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDepartment(null);
  };

  return (
    <>
      <PageTemplate
        title={t('erasmus.title')}
        subtitle={t('erasmus.subtitle')}
      >
        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg my-12 text-gray-700 leading-relaxed text-justify">
          <p className="mb-4">{t('erasmus.dearStudents')}</p>
          <p className="mb-4">{t('erasmus.intro1')}</p>
          <p className="mb-4">{t('erasmus.intro2')}</p>
          <p className="mb-6">{t('erasmus.intro3')}</p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg text-left">
            <h3 className="font-bold text-lg text-blue-800 mb-2">{t('erasmus.officialSourceTitle')}</h3>
            <p className="mb-4">{t('erasmus.officialSourceBody')}</p>
            <a href="https://abofisi.hacettepe.edu.tr/" target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
              {t('erasmus.goToEUOffice')}
            </a>
          </div>
          <p className="mt-6">{t('erasmus.outro1')}</p>
          <p className="mt-4 font-semibold text-right" dangerouslySetInnerHTML={{ __html: t('erasmus.regards') }} />
        </div>

        {/* Öğrenci Deneyimleri Special Section */}
        <div className="relative bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-3xl shadow-2xl overflow-hidden my-16">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4"></div>
          
          <div className="relative z-10 p-8 lg:p-12 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <span className="text-4xl">🎓</span>
                </div>
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {t('erasmus.studentExperiences')}
              </h2>
              
              <p className="text-lg lg:text-xl text-green-100 mb-8 leading-relaxed">
                {t('erasmus.studentExperiencesSubtitle')}
              </p>
              
              <div className="flex justify-center">
                <a 
                  href="/programlar/erasmus/ogrenci-deneyimleri" 
                  className="group inline-flex items-center justify-center bg-white text-green-700 font-bold py-4 px-8 rounded-2xl hover:bg-green-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span className="mr-2 text-2xl group-hover:scale-110 transition-transform duration-300">✨</span>
                  {t('erasmus.viewExperiences')}
                  <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 my-16">
          {programs.map((program, index) => (
            <motion.div
              key={program.name}
              className="rounded-2xl shadow-lg overflow-hidden flex flex-col group cursor-pointer"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              custom={index}
              whileHover={{ y: -8, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              transition={{ duration: 0.3 }}
              onClick={() => handleCardClick(program)}
            >
              <div
                className="h-48 bg-cover bg-center"
                style={{ backgroundImage: `url(${program.image})` }}
              />
              <div className={`p-8 flex-grow flex flex-col text-white ${program.bgColor}`}>
                <h3 className="text-2xl font-bold tracking-wide">{program.name}</h3>
                <p className="text-white/80 mt-2">{t('erasmus.clickForUniversities')}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </PageTemplate>

      {isModalOpen && selectedDepartment && (
        <UniversityModal
          department={selectedDepartment.name}
          universities={selectedDepartment.universities}
          coordinator={selectedDepartment.coordinator}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default ErasmusPage;